import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Shield, 
  Mail, 
  Key, 
  CheckCircle, 
  XCircle,
  Loader2,
  LogOut,
  Eye,
  EyeOff,
  AlertTriangle,
  Users,
  Trash2,
  Scissors,
  PlusCircle,
  CalendarDays,
  RefreshCw,
  Pencil,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

// Use relative URL if REACT_APP_BACKEND_URL is not set (same-origin deployment)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_CODE = 'KyberCosAdmin2026!';

const AdminPage = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  
  // SMTP Settings
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    use_tls: true
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  
  // Captcha Settings
  const [captchaSettings, setCaptchaSettings] = useState({
    site_key: '',
    secret_key: '',
    enabled: false
  });
  const [captchaConfigured, setCaptchaConfigured] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // User Management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Creator Role Management
  const [creatorDialog, setCreatorDialog] = useState({ open: false, user: null });
  const [creatorUsername, setCreatorUsername] = useState('');
  const [creatorLoading, setCreatorLoading] = useState(false);

  // Creator Applications
  const [applications, setApplications] = useState([]);
  const [approveDialog, setApproveDialog] = useState({ open: false, app: null });
  const [approveUsername, setApproveUsername] = useState('');
  const [appActionLoading, setAppActionLoading] = useState(false);

  // Cons Directory
  const [dirEntries, setDirEntries] = useState([]);
  const [dirTotal, setDirTotal] = useState(0);
  const [dirQuery, setDirQuery] = useState('');
  const [dirLoading, setDirLoading] = useState(false);
  const [dirSyncStatus, setDirSyncStatus] = useState(null);
  const [dirSyncing, setDirSyncing] = useState(false);
  const [dirConflicts, setDirConflicts] = useState([]);
  const [dirEditing, setDirEditing] = useState(null);
  const [dirForm, setDirForm] = useState(null);
  const [dirSaving, setDirSaving] = useState(false);

  const api = useCallback(() => {
    return axios.create({
      baseURL: `${BACKEND_URL}/api`,
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json'
      }
    });
  }, [adminToken]);

  const fetchSettings = useCallback(async () => {
    if (!adminToken) return;
    
    try {
      const [smtpRes, captchaRes, usersRes, appsRes, dirRes, syncRes, conflictRes] = await Promise.all([
        api().get('/admin/settings/smtp'),
        api().get('/admin/settings/captcha'),
        api().get('/admin/users'),
        api().get('/admin/creator-applications?status=pending'),
        api().get('/admin/cons/directory', { params: { limit: 50 } }),
        api().get('/admin/cons/sync-status'),
        api().get('/admin/cons/directory/conflicts')
      ]);
      
      if (smtpRes.data.configured) {
        setSmtpSettings(prev => ({
          ...prev,
          host: smtpRes.data.host || '',
          port: smtpRes.data.port || 587,
          username: smtpRes.data.username || '',
          from_email: smtpRes.data.from_email || '',
          use_tls: smtpRes.data.use_tls ?? true
        }));
        setSmtpConfigured(true);
      }
      
      if (captchaRes.data.configured) {
        setCaptchaSettings(prev => ({
          ...prev,
          site_key: captchaRes.data.site_key || '',
          enabled: captchaRes.data.enabled || false
        }));
        setCaptchaConfigured(true);
      }

      setUsers(usersRes.data || []);
      setApplications(appsRes.data || []);
      setDirEntries(dirRes.data?.entries || []);
      setDirTotal(dirRes.data?.total || 0);
      setDirSyncStatus(syncRes.data || null);
      setDirConflicts(conflictRes.data || []);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  }, [adminToken, api]);

  // ---- Cons Directory ----

  // FastAPI returns a 422 `detail` as an ARRAY; passing that straight to
  // toast.error renders an object as a child and blanks the whole SPA.
  const errText = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  };

  const fetchDirectory = async (q = dirQuery) => {
    setDirLoading(true);
    try {
      const res = await api().get('/admin/cons/directory', {
        params: { q: q.trim(), limit: 50 }
      });
      setDirEntries(res.data?.entries || []);
      setDirTotal(res.data?.total || 0);
    } catch (error) {
      toast.error(errText(error, 'Failed to load the directory'));
    } finally {
      setDirLoading(false);
    }
  };

  const refreshSyncStatus = async () => {
    try {
      const [syncRes, conflictRes] = await Promise.all([
        api().get('/admin/cons/sync-status'),
        api().get('/admin/cons/directory/conflicts')
      ]);
      setDirSyncStatus(syncRes.data || null);
      setDirConflicts(conflictRes.data || []);
    } catch (error) {
      console.error('Failed to refresh sync status');
    }
  };

  const handleSyncNow = async () => {
    setDirSyncing(true);
    try {
      await api().post('/admin/cons/directory/sync');
      toast.success('Sync started — it walks ~63 state pages, so give it a minute.');
      // The run is a background task; poll once it has had time to finish.
      setTimeout(() => { refreshSyncStatus(); fetchDirectory(); }, 90000);
    } catch (error) {
      toast.error(errText(error, 'Could not start the sync'));
    } finally {
      setDirSyncing(false);
    }
  };

  const startCreateEntry = () => {
    setDirEditing('new');
    setDirForm({ name: '', venue: '', city: '', state: '', start_date: '', end_date: '', website: '', source_url: '' });
  };

  const startEditEntry = (entry) => {
    setDirEditing(entry.source_id);
    setDirForm({
      name: entry.name || '', venue: entry.venue || '', city: entry.city || '',
      state: entry.state || '', start_date: entry.start_date || '', end_date: entry.end_date || '',
      website: entry.website || '', source_url: entry.source_url || ''
    });
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!dirForm?.name?.trim()) return;
    setDirSaving(true);
    try {
      if (dirEditing === 'new') {
        await api().post('/admin/cons/directory', dirForm);
        toast.success('Directory entry added');
      } else {
        await api().put(`/admin/cons/directory/${encodeURIComponent(dirEditing)}`, dirForm);
        toast.success('Entry saved — your edits are now pinned against future syncs');
      }
      setDirEditing(null);
      setDirForm(null);
      await fetchDirectory();
      await refreshSyncStatus();
    } catch (error) {
      toast.error(errText(error, 'Could not save the entry'));
    } finally {
      setDirSaving(false);
    }
  };

  const handleDeleteEntry = async (sourceId, name) => {
    try {
      await api().delete(`/admin/cons/directory/${encodeURIComponent(sourceId)}`);
      toast.success(`"${name}" removed from the directory`);
      setDirEntries(dirEntries.filter(e => e.source_id !== sourceId));
      setDirTotal(t => Math.max(0, t - 1));
    } catch (error) {
      toast.error(errText(error, 'Could not delete the entry'));
    }
  };

  const handleDeleteUser = async (userId, displayName) => {
    setUsersLoading(true);
    try {
      await api().delete(`/admin/users/${userId}`);
      toast.success(`User "${displayName}" deleted successfully`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleGrantCreatorRole = async () => {
    if (!creatorDialog.user || !creatorUsername.trim()) {
      toast.error('Please enter a creator username');
      return;
    }
    
    setCreatorLoading(true);
    try {
      await api().put(`/admin/users/${creatorDialog.user.id}/creator-role`, {
        creator_role: true,
        creator_username: creatorUsername.trim()
      });
      
      // Update local users list
      setUsers(users.map(u => 
        u.id === creatorDialog.user.id 
          ? { ...u, creator_role: true, creator_username: creatorUsername.trim() }
          : u
      ));
      
      toast.success(`Creator role granted to ${creatorDialog.user.display_name}`);
      setCreatorDialog({ open: false, user: null });
      setCreatorUsername('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant creator role');
    } finally {
      setCreatorLoading(false);
    }
  };

  const handleRevokeCreatorRole = async (user) => {
    setCreatorLoading(true);
    try {
      await api().put(`/admin/users/${user.id}/creator-role`, {
        creator_role: false
      });
      
      // Update local users list
      setUsers(users.map(u => 
        u.id === user.id 
          ? { ...u, creator_role: false, creator_username: null }
          : u
      ));
      
      toast.success(`Creator role revoked from ${user.display_name}`);
    } catch (error) {
      toast.error('Failed to revoke creator role');
    } finally {
      setCreatorLoading(false);
    }
  };

  const slugify = (s) => (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);

  const socialUrl = (platform, value) => {
    const v = (value || '').trim().replace(/^@/, '');
    if (/^https?:\/\//i.test(value || '')) return value;
    switch (platform) {
      case 'instagram': return `https://instagram.com/${v}`;
      case 'tiktok': return `https://tiktok.com/@${v}`;
      case 'youtube': return `https://youtube.com/@${v}`;
      case 'facebook': return `https://facebook.com/${v}`;
      default: return value;
    }
  };

  const openApprove = (app) => {
    setApproveUsername(slugify(app.name));
    setApproveDialog({ open: true, app });
  };

  const handleApproveApplication = async () => {
    if (!approveDialog.app || !approveUsername.trim()) {
      toast.error('Please enter a creator username');
      return;
    }
    setAppActionLoading(true);
    try {
      await api().post(`/admin/creator-applications/${approveDialog.app.id}/approve`, {
        creator_username: approveUsername.trim()
      });
      toast.success(`Approved ${approveDialog.app.name} as a creator`);
      setApplications(applications.filter(a => a.id !== approveDialog.app.id));
      setApproveDialog({ open: false, app: null });
      setApproveUsername('');
      fetchSettings(); // refresh users list so the new creator shows
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setAppActionLoading(false);
    }
  };

  const handleDenyApplication = async (app) => {
    setAppActionLoading(true);
    try {
      await api().post(`/admin/creator-applications/${app.id}/deny`);
      toast.success(`Denied ${app.name}'s application`);
      setApplications(applications.filter(a => a.id !== app.id));
    } catch (error) {
      toast.error('Failed to deny application');
    } finally {
      setAppActionLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated && adminToken) {
      fetchSettings();
    }
  }, [authenticated, adminToken, fetchSettings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        code: adminCode
      });
      
      setAdminToken(response.data.access_token);
      setAuthenticated(true);
      toast.success('Admin access granted');
    } catch (error) {
      toast.error('Invalid admin code');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setAdminToken(null);
    setAdminCode('');
  };

  const handleSaveSMTP = async (e) => {
    e.preventDefault();
    setSmtpLoading(true);
    
    try {
      await api().post('/admin/settings/smtp', smtpSettings);
      toast.success('SMTP settings saved!');
      setSmtpConfigured(true);
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleTestSMTP = async () => {
    setSmtpTesting(true);
    
    try {
      const response = await api().post('/admin/settings/smtp/test');
      if (response.data.success) {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(`SMTP test failed: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('SMTP test failed');
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSaveCaptcha = async (e) => {
    e.preventDefault();
    setCaptchaLoading(true);
    
    try {
      await api().post('/admin/settings/captcha', captchaSettings);
      toast.success('Captcha settings saved!');
      setCaptchaConfigured(true);
    } catch (error) {
      toast.error('Failed to save captcha settings');
    } finally {
      setCaptchaLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-slate-950" />
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-white">Admin Panel</h1>
            </div>
            <p className="text-slate-400">Enter admin code to access</p>
          </div>
          
          <div className="glass-card p-8 border-amber-500/20">
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="adminCode" className="text-slate-300">Admin Code</Label>
                <div className="relative">
                  <Input
                    id="adminCode"
                    type={showCode ? 'text' : 'password'}
                    value={adminCode}
                    onChange={(e) => setAdminCode(e.target.value)}
                    placeholder="Enter admin code"
                    required
                    className="bg-slate-950/50 border-white/10 focus:border-amber-500/50 rounded-xl h-12 pr-12"
                    data-testid="admin-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCode(!showCode)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-amber-400 transition-colors"
                  >
                    {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-white font-semibold transition-all"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Access Admin Panel'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 lg:p-8" data-testid="admin-panel">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-white">Admin Panel</h1>
              <p className="text-slate-400 text-sm">System Configuration</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-950/20"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>

        {/* Creator Applications */}
        <div className="glass-card p-6 border-fuchsia-500/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-500/20 flex items-center justify-center">
              <PlusCircle className="w-5 h-5 text-fuchsia-400" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-white">Creator Applications</h2>
              <p className="text-sm text-slate-400">{applications.length} pending review</p>
            </div>
            {applications.length > 0 && (
              <span className="ml-auto flex items-center gap-1 text-xs text-fuchsia-300 bg-fuchsia-500/20 px-2.5 py-1 rounded-full">
                {applications.length} awaiting
              </span>
            )}
          </div>

          {applications.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No pending applications</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="p-4 bg-slate-900/50 rounded-xl"
                  data-testid={`application-${app.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white font-medium">{app.name}</p>
                      <p className="text-sm text-slate-400 break-all">{app.email}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(app.socials || {}).map(([platform, value]) => value ? (
                          <a
                            key={platform}
                            href={socialUrl(platform, value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-cyan-400 hover:text-cyan-300 bg-cyan-500/10 px-2 py-1 rounded-full capitalize break-all"
                          >
                            {platform}: {value}
                          </a>
                        ) : null)}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Applied {new Date(app.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        disabled={appActionLoading}
                        onClick={() => openApprove(app)}
                        className="rounded-lg bg-emerald-500/90 hover:bg-emerald-400 text-white"
                        data-testid={`approve-app-${app.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={appActionLoading}
                        onClick={() => handleDenyApplication(app)}
                        className="rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-950/30"
                        data-testid={`deny-app-${app.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Deny
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Approve dialog */}
          <Dialog
            open={approveDialog.open}
            onOpenChange={(open) => {
              setApproveDialog({ open, app: open ? approveDialog.app : null });
              if (!open) setApproveUsername('');
            }}
          >
            <DialogContent className="bg-slate-900 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Approve Creator Application</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Grant {approveDialog.app?.name} the creator role. Their submitted socials will prefill their creator profile.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Creator Username</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Public profile will be available at links.kybercosplay.com/@username
                  </p>
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-slate-800 rounded-l-xl border border-white/10 text-slate-400">@</span>
                    <Input
                      value={approveUsername}
                      onChange={(e) => setApproveUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase())}
                      placeholder="username"
                      className="bg-slate-950/50 border-white/10 rounded-l-none rounded-r-xl"
                      data-testid="approve-username-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleApproveApplication}
                  disabled={appActionLoading || !approveUsername.trim()}
                  className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400"
                  data-testid="confirm-approve-app-btn"
                >
                  {appActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve & Grant Creator Role'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* SMTP Settings */}
        <div className="glass-card p-6 border-amber-500/20">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-6 h-6 text-amber-400" />
            <h2 className="text-xl font-heading font-semibold text-white">SMTP Settings</h2>
            {smtpConfigured ? (
              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded-full">
                <CheckCircle className="w-3 h-3" />
                Configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/20 px-2 py-1 rounded-full">
                <AlertTriangle className="w-3 h-3" />
                Not Configured
              </span>
            )}
          </div>
          
          <div className="mb-4 p-4 bg-slate-900/50 rounded-xl border border-white/5">
            <p className="text-sm text-slate-400">
              <strong className="text-slate-300">Proton Mail SMTP Settings:</strong><br />
              Host: smtp.protonmail.ch<br />
              Port: 587 (TLS) or 465 (SSL)<br />
              You'll need a Proton Mail Bridge or paid plan for SMTP access.
            </p>
          </div>
          
          <form onSubmit={handleSaveSMTP} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">SMTP Host</Label>
                <Input
                  value={smtpSettings.host}
                  onChange={(e) => setSmtpSettings({...smtpSettings, host: e.target.value})}
                  placeholder="smtp.protonmail.ch"
                  required
                  className="bg-slate-950/50 border-white/10 rounded-xl"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Port</Label>
                <Input
                  type="number"
                  value={smtpSettings.port}
                  onChange={(e) => setSmtpSettings({...smtpSettings, port: parseInt(e.target.value)})}
                  placeholder="587"
                  required
                  className="bg-slate-950/50 border-white/10 rounded-xl font-mono"
                  data-testid="smtp-port-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Username</Label>
                <Input
                  value={smtpSettings.username}
                  onChange={(e) => setSmtpSettings({...smtpSettings, username: e.target.value})}
                  placeholder="your@protonmail.com"
                  required
                  className="bg-slate-950/50 border-white/10 rounded-xl"
                  data-testid="smtp-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password</Label>
                <Input
                  type="password"
                  value={smtpSettings.password}
                  onChange={(e) => setSmtpSettings({...smtpSettings, password: e.target.value})}
                  placeholder="••••••••"
                  required={!smtpConfigured}
                  className="bg-slate-950/50 border-white/10 rounded-xl"
                  data-testid="smtp-password-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">From Email</Label>
              <Input
                type="email"
                value={smtpSettings.from_email}
                onChange={(e) => setSmtpSettings({...smtpSettings, from_email: e.target.value})}
                placeholder="noreply@yourdomain.com"
                required
                className="bg-slate-950/50 border-white/10 rounded-xl"
                data-testid="smtp-from-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
              <div>
                <Label className="text-slate-300">Use TLS</Label>
                <p className="text-xs text-slate-500">Enable STARTTLS encryption</p>
              </div>
              <Switch
                checked={smtpSettings.use_tls}
                onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, use_tls: checked})}
                data-testid="smtp-tls-switch"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={handleTestSMTP}
                disabled={smtpTesting || !smtpConfigured}
                variant="outline"
                className="flex-1 rounded-xl border-white/10 text-slate-300 hover:bg-white/5"
                data-testid="smtp-test-btn"
              >
                {smtpTesting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Test Connection'}
              </Button>
              <Button
                type="submit"
                disabled={smtpLoading}
                className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold"
                data-testid="smtp-save-btn"
              >
                {smtpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save SMTP Settings'}
              </Button>
            </div>
          </form>
        </div>

        {/* Captcha Settings */}
        <div className="glass-card p-6 border-amber-500/20">
          <div className="flex items-center gap-3 mb-6">
            <Key className="w-6 h-6 text-amber-400" />
            <h2 className="text-xl font-heading font-semibold text-white">Google reCAPTCHA</h2>
            {captchaConfigured && captchaSettings.enabled ? (
              <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded-full">
                <CheckCircle className="w-3 h-3" />
                Enabled
              </span>
            ) : captchaConfigured ? (
              <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-500/20 px-2 py-1 rounded-full">
                <XCircle className="w-3 h-3" />
                Disabled
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/20 px-2 py-1 rounded-full">
                <AlertTriangle className="w-3 h-3" />
                Not Configured
              </span>
            )}
          </div>
          
          <div className="mb-4 p-4 bg-slate-900/50 rounded-xl border border-white/5">
            <p className="text-sm text-slate-400">
              Get your reCAPTCHA v3 keys from{' '}
              <a 
                href="https://www.google.com/recaptcha/admin" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300"
              >
                Google reCAPTCHA Admin Console
              </a>
            </p>
          </div>
          
          <form onSubmit={handleSaveCaptcha} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-300">Site Key</Label>
              <Input
                value={captchaSettings.site_key}
                onChange={(e) => setCaptchaSettings({...captchaSettings, site_key: e.target.value})}
                placeholder="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                className="bg-slate-950/50 border-white/10 rounded-xl font-mono text-sm"
                data-testid="captcha-sitekey-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Secret Key</Label>
              <Input
                type="password"
                value={captchaSettings.secret_key}
                onChange={(e) => setCaptchaSettings({...captchaSettings, secret_key: e.target.value})}
                placeholder="••••••••"
                className="bg-slate-950/50 border-white/10 rounded-xl"
                data-testid="captcha-secretkey-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
              <div>
                <Label className="text-slate-300">Enable Captcha</Label>
                <p className="text-xs text-slate-500">Require captcha verification on registration</p>
              </div>
              <Switch
                checked={captchaSettings.enabled}
                onCheckedChange={(checked) => setCaptchaSettings({...captchaSettings, enabled: checked})}
                data-testid="captcha-enabled-switch"
              />
            </div>
            
            <Button
              type="submit"
              disabled={captchaLoading}
              className="w-full rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold"
              data-testid="captcha-save-btn"
            >
              {captchaLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Captcha Settings'}
            </Button>
          </form>
        </div>

        {/* User Management */}
        <div className="glass-card p-6 border-amber-500/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-white">User Management</h2>
              <p className="text-sm text-slate-400">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {users.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No users registered yet</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {users.map((user) => (
                <div 
                  key={user.id} 
                  className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl"
                  data-testid={`user-${user.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center text-white font-semibold">
                      {user.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.display_name}</p>
                      <p className="text-sm text-slate-400">{user.email}</p>
                      {user.creator_role && (
                        <span className="inline-flex items-center gap-1 text-xs text-fuchsia-400 bg-fuchsia-500/20 px-2 py-0.5 rounded-full mt-1">
                          <PlusCircle className="w-3 h-3" />
                          Creator: @{user.creator_username}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm text-slate-400">
                        <Scissors className="w-4 h-4" />
                        <span>{user.cosplay_count} cosplay{user.cosplay_count !== 1 ? 's' : ''}</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Joined {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    {/* Creator Role Toggle */}
                    {user.creator_role ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={creatorLoading}
                        onClick={() => handleRevokeCreatorRole(user)}
                        className="text-fuchsia-400 hover:text-fuchsia-300 hover:bg-fuchsia-950/30 rounded-lg"
                        data-testid={`revoke-creator-${user.id}`}
                      >
                        Revoke Creator
                      </Button>
                    ) : (
                      <Dialog open={creatorDialog.open && creatorDialog.user?.id === user.id} onOpenChange={(open) => {
                        setCreatorDialog({ open, user: open ? user : null });
                        if (!open) setCreatorUsername('');
                      }}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-950/30 rounded-lg"
                            data-testid={`grant-creator-${user.id}`}
                          >
                            <PlusCircle className="w-4 h-4 mr-1" />
                            Grant Creator
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-white/10">
                          <DialogHeader>
                            <DialogTitle className="text-white">Grant Creator Role</DialogTitle>
                            <DialogDescription className="text-slate-400">
                              This will allow {user.display_name} to create and manage a public link landing page.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-slate-300">Creator Username</Label>
                              <p className="text-xs text-slate-500 mb-2">
                                Public profile will be available at links.kybercosplay.com/@username
                              </p>
                              <div className="flex items-center">
                                <span className="px-3 py-2 bg-slate-800 rounded-l-xl border border-white/10 text-slate-400">@</span>
                                <Input
                                  value={creatorUsername}
                                  onChange={(e) => setCreatorUsername(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase())}
                                  placeholder="username"
                                  className="bg-slate-950/50 border-white/10 rounded-l-none rounded-r-xl"
                                  data-testid="creator-username-input"
                                />
                              </div>
                            </div>
                            <Button
                              onClick={handleGrantCreatorRole}
                              disabled={creatorLoading || !creatorUsername.trim()}
                              className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400"
                              data-testid="confirm-grant-creator-btn"
                            >
                              {creatorLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Grant Creator Role'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={usersLoading}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-950/30 rounded-lg"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete User?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            This will permanently delete <strong className="text-white">{user.display_name}</strong> ({user.email}) and all their cosplays, tasks, photos, receipts, and time logs.
                            <br /><br />
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-xl bg-transparent border-white/10 text-slate-300 hover:bg-white/5">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteUser(user.id, user.display_name)}
                            className="rounded-xl bg-red-500 hover:bg-red-400 text-white"
                          >
                            Delete User
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cons Directory */}
        <div className="glass-card p-6 border-amber-500/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-heading font-semibold text-white">Cons Directory</h2>
              <p className="text-sm text-slate-400">
                {dirSyncStatus?.total_entries ?? 0} entries · {dirSyncStatus?.upcoming_entries ?? 0} upcoming
                {dirSyncStatus?.curated_entries ? ` · ${dirSyncStatus.curated_entries} hand-edited` : ''}
              </p>
            </div>
          </div>

          {/* Sync status */}
          <div className="p-4 bg-slate-900/50 rounded-xl mb-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                {dirSyncStatus?.is_stale ? (
                  <p className="text-sm font-semibold text-red-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    Directory is stale
                  </p>
                ) : (
                  <p className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 shrink-0" />
                    Directory is fresh
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  {dirSyncStatus?.last_success_age_hours == null
                    ? 'No successful sync recorded yet.'
                    : `Last successful sync ${dirSyncStatus.last_success_age_hours}h ago.`}
                  {dirSyncStatus?.status?.parsed != null &&
                    ` Parsed ${dirSyncStatus.status.parsed} events from ${dirSyncStatus.status.states_fetched}/${dirSyncStatus.status.states_expected} states.`}
                </p>
                {dirSyncStatus?.status?.skipped_unparsed > 0 && (
                  <p className="text-xs text-amber-400 mt-1">
                    {dirSyncStatus.status.skipped_unparsed} row(s) had unreadable dates and were skipped.
                  </p>
                )}
                {dirSyncStatus?.status?.error && (
                  <p className="text-xs text-red-400 mt-1 break-words">
                    Last error: {dirSyncStatus.status.error}
                  </p>
                )}
                {dirSyncStatus?.by_source && (
                  <p className="text-xs text-slate-500 mt-1">
                    {Object.entries(dirSyncStatus.by_source).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                  </p>
                )}
              </div>
              <Button
                onClick={handleSyncNow}
                disabled={dirSyncing || dirSyncStatus?.running}
                className="rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold shrink-0"
                data-testid="sync-directory-now"
              >
                {dirSyncing || dirSyncStatus?.running
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing…</>
                  : <><RefreshCw className="w-4 h-4 mr-2" /> Sync now</>}
              </Button>
            </div>
          </div>

          {/* Duplicate listings needing a decision */}
          {dirConflicts.length > 0 && (
            <div className="p-4 bg-amber-950/20 border border-amber-500/20 rounded-xl mb-4">
              <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {dirConflicts.length} duplicate listing{dirConflicts.length === 1 ? '' : 's'}
              </p>
              <p className="text-xs text-slate-400 mt-1 mb-3">
                The same con appears more than once upstream with differing details. These are never merged
                automatically — delete the wrong one, or edit the right one to pin it.
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {dirConflicts.map((c) => (
                  <div key={c.key} className="text-xs text-slate-300 bg-slate-900/50 rounded-lg p-2.5">
                    <span className="font-semibold">{c.name}</span> · {c.start_date}
                    <ul className="mt-1 space-y-0.5">
                      {c.entries.map((e) => (
                        <li key={e.source_id} className="text-slate-400">
                          {e.source_id} — {e.venue || 'no venue'}{e.city ? `, ${e.city}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search + add */}
          <div className="flex gap-2 mb-4">
            <Input
              value={dirQuery}
              onChange={(e) => setDirQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fetchDirectory(); } }}
              placeholder="Search the directory…"
              className="bg-slate-950/50 border-white/10 rounded-xl text-white"
              data-testid="directory-search"
            />
            <Button
              onClick={() => fetchDirectory()}
              disabled={dirLoading}
              variant="ghost"
              className="rounded-xl border border-white/10 text-slate-300 hover:bg-white/5 shrink-0"
            >
              {dirLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
            <Button
              onClick={startCreateEntry}
              className="rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold shrink-0"
              data-testid="directory-add"
            >
              <PlusCircle className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>

          {/* Create / edit form */}
          {dirForm && (
            <form onSubmit={handleSaveEntry} className="p-4 bg-slate-900/50 rounded-xl mb-4 space-y-3">
              <p className="text-sm font-semibold text-white">
                {dirEditing === 'new' ? 'New directory entry' : `Editing ${dirEditing}`}
              </p>
              <Input
                value={dirForm.name}
                onChange={(e) => setDirForm({ ...dirForm, name: e.target.value })}
                placeholder="Convention name *"
                required
                className="bg-slate-950/50 border-white/10 rounded-xl text-white"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input value={dirForm.venue} onChange={(e) => setDirForm({ ...dirForm, venue: e.target.value })}
                  placeholder="Venue" className="bg-slate-950/50 border-white/10 rounded-xl text-white" />
                <Input value={dirForm.city} onChange={(e) => setDirForm({ ...dirForm, city: e.target.value })}
                  placeholder="City" className="bg-slate-950/50 border-white/10 rounded-xl text-white" />
                <Input value={dirForm.state} onChange={(e) => setDirForm({ ...dirForm, state: e.target.value })}
                  placeholder="State (e.g. OH)" className="bg-slate-950/50 border-white/10 rounded-xl text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-400">Start</Label>
                  <Input type="date" value={dirForm.start_date}
                    onChange={(e) => setDirForm({ ...dirForm, start_date: e.target.value })}
                    className="bg-slate-950/50 border-white/10 rounded-xl text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">End</Label>
                  <Input type="date" value={dirForm.end_date}
                    onChange={(e) => setDirForm({ ...dirForm, end_date: e.target.value })}
                    className="bg-slate-950/50 border-white/10 rounded-xl text-white mt-1" />
                </div>
              </div>
              <Input value={dirForm.website} onChange={(e) => setDirForm({ ...dirForm, website: e.target.value })}
                placeholder="Official website (https://…)" className="bg-slate-950/50 border-white/10 rounded-xl text-white" />
              <p className="text-xs text-slate-500">
                Any field you change here is pinned and will not be overwritten by a future sync.
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="ghost"
                  onClick={() => { setDirEditing(null); setDirForm(null); }}
                  className="rounded-xl border border-white/10 text-slate-300 hover:bg-white/5">
                  Cancel
                </Button>
                <Button type="submit" disabled={dirSaving}
                  className="rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold flex-1">
                  {dirSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </form>
          )}

          {/* Entries */}
          {dirLoading && dirEntries.length === 0 ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : dirEntries.length === 0 ? (
            <p className="text-slate-400 text-center py-8">
              {dirQuery ? 'No entries match that search' : 'The directory is empty — run a sync or add an entry'}
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-2">
                Showing {dirEntries.length} of {dirTotal} upcoming
              </p>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {dirEntries.map((entry) => (
                  <div
                    key={entry.source_id}
                    className="flex items-center justify-between gap-3 p-4 bg-slate-900/50 rounded-xl"
                    data-testid={`directory-entry-${entry.source_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {entry.name}
                        {entry.is_curated && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-cyan-400">edited</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {[entry.start_date, [entry.venue, entry.city, entry.state].filter(Boolean).join(', ')]
                          .filter(Boolean).join(' · ')}
                      </p>
                      <p className="text-[10px] text-slate-600 truncate">{entry.source_id}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEditEntry(entry)}
                        className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-950/30 rounded-lg"
                        data-testid={`edit-entry-${entry.source_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm"
                            className="text-slate-400 hover:text-red-400 hover:bg-red-950/30 rounded-lg"
                            data-testid={`delete-entry-${entry.source_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-900 border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Delete directory entry?</AlertDialogTitle>
                            <AlertDialogDescription className="text-slate-400">
                              Removes "{entry.name}" from the searchable directory. Conventions users have
                              already added to their own list are not affected. A synced entry will come back
                              on the next sync unless the upstream listing is gone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-xl bg-transparent border-white/10 text-slate-300 hover:bg-white/5">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteEntry(entry.source_id, entry.name)}
                              className="rounded-xl bg-red-500 hover:bg-red-400 text-white">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
